/**
 * useUserStore — Zustand store for user profile and session data
 *
 * Manages: user profile, authentication state, premium status.
 * Provides actions for login/logout, profile updates, and dark mode toggle.
 * Replaces the massive useState<UserProfile> in App.tsx and eliminates
 * prop-drilling of `user` and `onUpdateUser` through 10+ components.
 */
import { create } from 'zustand';
import type { UserProfile, Badge, Question } from '../../shared/types';
import { ApiClient } from '../services/apiClient';
import { setSentryUser } from '../index';
import logger from '../services/logger';

interface SessionData {
  questions: Question[];
  mode: 'learn' | 'exam' | 'mistakes' | 'srs' | 'bookmarks' | 'smart' | 'placement';
  chapterId?: number;
  /** True when this is a free-tier preview slice of a locked chapter. */
  isPreview?: boolean;
  /**
   * Present only when restoring an interrupted session from its snapshot
   * (F-09). Regular session starts never set this.
   */
  resume?: {
    index: number;
    answers: Record<number, string[]>;
    submitted: Record<number, boolean>;
    examTimeLeft: number;
  };
}

interface ExamResult {
  correctCount: number;
  errorPoints: number;
  passed: boolean;
  chapterErrors: Record<number, number>;
  timeUsed?: number;
  /** Questions in this run — the result screen used to hardcode 30. */
  totalQuestions?: number;
  /** Wrong 5-point questions; two of them fail the exam regardless of
   * total error points (official rule, F-18). */
  fivePointErrors?: number;
}

export interface PlacementResultData {
  chapterResults: { chapterId: number; correct: boolean }[];
  totalCorrect: number;
  totalQuestions: number;
}

interface UserState {
  // User Profile
  user: UserProfile | null;
  isPremium: boolean;

  // Session
  sessionData: SessionData | null;
  examResult: ExamResult | null;
  placementResult: PlacementResultData | null;

  // Badge notification
  newBadge: Badge | null;

  // Essences version (triggers re-render when essences load)
  essencesVersion: number;

  // Actions — User
  setUser: (user: UserProfile | null) => void;
  updateUser: (updates: Partial<UserProfile>) => void;
  refreshFromSupabase: () => Promise<void>;

  // Actions — Auth
  handleLogin: () => void;
  handleLogout: () => Promise<void>;
  syncFromApiClient: () => void;

  // Actions — Dark Mode
  toggleDarkMode: () => void;
  initDarkMode: (isDark: boolean) => void;

  // Actions — Session
  setSessionData: (data: SessionData | null) => void;
  setExamResult: (result: ExamResult | null) => void;
  setPlacementResult: (result: PlacementResultData | null) => void;

  // Actions — Badge
  setNewBadge: (badge: Badge | null) => void;

  // Actions — Essences
  bumpEssencesVersion: () => void;
}

export const useUserStore = create<UserState>((set, get) => ({
  // ── Initial State ──────────────────────────
  user: null,
  isPremium: false,
  sessionData: null,
  examResult: null,
  placementResult: null,
  newBadge: null,
  essencesVersion: 0,

  // ── Actions ────────────────────────────────
  setUser: (user) =>
    set({
      user,
      isPremium: user?.isPremium === true,
    }),

  updateUser: (updates) => {
    const updated = ApiClient.updateUser(updates);
    set({ user: updated, isPremium: updated.isPremium === true });
    return updated;
  },

  refreshFromSupabase: async () => {
    const freshUser = await ApiClient.refreshUserFromSupabase();
    if (freshUser) {
      // Note: we used to detect a premium upgrade here by comparing
      // freshUser.isPremium to currentUser.isPremium and firing the
      // celebration modal. That misfired on every page-load of an
      // already-premium user whose localStorage was momentarily out
      // of sync with the DB — leading to the "all modals at once"
      // login experience. The real celebration fires from the
      // CopeCart webhook success flow.
      set({ user: freshUser, isPremium: freshUser.isPremium === true });
      logger.info('UserStore', 'User data refreshed from Supabase');
    }
  },

  handleLogin: () => {
    const u = ApiClient.getUser();
    set({ user: u, isPremium: u.isPremium === true });

    // Initialize dark mode
    if (u.isDarkMode) {
      document.documentElement.classList.add('dark');
    }
  },

  handleLogout: async () => {
    await ApiClient.logout();
    set({
      user: null,
      isPremium: false,
      sessionData: null,
      examResult: null,
    });
    setSentryUser(null);
  },

  syncFromApiClient: () => {
    const { user: currentUser } = get();
    const freshUser = ApiClient.getUser();

    // Check for new badges
    if (currentUser) {
      const oldBadgeIds = currentUser.badges.map((b) => b.id);
      const newBadges = freshUser.badges.filter((b) => !oldBadgeIds.includes(b.id));
      if (newBadges.length > 0) {
        set({ newBadge: newBadges[0] });
      }
    }

    set({ user: freshUser, isPremium: freshUser.isPremium === true });
  },

  toggleDarkMode: () => {
    const { user } = get();
    if (!user) return;

    const newDark = !user.isDarkMode;
    const updated = ApiClient.updateUser({ isDarkMode: newDark });
    set({ user: updated });
    localStorage.setItem('fahryo_dark_mode', newDark ? '1' : '0');

    if (newDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  },

  initDarkMode: (isDark) => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  },

  setSessionData: (data) => set({ sessionData: data }),
  setExamResult: (result) => set({ examResult: result }),
  setPlacementResult: (result) => set({ placementResult: result }),
  setNewBadge: (badge) => set({ newBadge: badge }),
  bumpEssencesVersion: () => set((state) => ({ essencesVersion: state.essencesVersion + 1 })),
}));
